import { useEffect, useLayoutEffect } from "react";
import Frame from "./Frame";
import { TOOL_MODE } from "../Constants";
import Draggable from "../Controls/Draggable";
import { calculateAttached } from "../Utils/frameUtils";
import { useStore } from "../Store/zustandStore";
import { useQueryClient } from "react-query";
import { configuratorQueryKeys } from "../../../react-query/queryConstants";
import {
  createEdges,
  edgesToSVG,
  collectAllProjectionBounds,
} from "../Utils/edgeUtils";
import { useThree } from "@react-three/fiber";
import { useProject, useReferrer } from "../../../hooks/useQueries";

export default function Unit({
  setIsDragging,
  isDragging,
  frames,
  setPickup,
  pickup,
  updateUnit,
  draggingEnabled,
}) {
  const [
    toolMode,
    setToolMode,
    selectedUUID,
    setSelectedUUID,
    frameThickness,
    setSelectedComponentInstance,
    requestDrawings,
    setRequestDrawings,
    setDrawings,
    drawingTolerance,
    setFramesAttachedTogether,
    setSelectedUUIDIsInSection,
    setShowConfigureTab,
    showConfigureTab,
  ] = useStore((state) => [
    state.toolMode,
    state.setToolMode,
    state.selectedUUID,
    state.setSelectedUUID,
    state.frameThickness,
    state.setSelectedComponentInstance,
    state.requestDrawings,
    state.setRequestDrawings,
    state.setDrawings,
    state.drawingTolerance,
    state.setFramesAttachedTogether,
    state.setSelectedUUIDIsInSection,
    state.setShowConfigureTab,
    state.showConfigureTab,
  ]);
  const { scene } = useThree();

  const queryClient = useQueryClient();

  const { updateProjectItem } = useProject();

  const referrer = useReferrer();

  useEffect(() => {
    updateUnit("FRAMES", -1, calculateAttached(frames, frameThickness));
  }, []);

  useEffect(() => {
    if (selectedUUID == null || selectedUUID.length == 0)
      setToolMode(TOOL_MODE.SELECTION);
  }, [selectedUUID]);

  // useLayoutEffect(() => {
  //   setRequestDrawings(true);
  //   console.log("Children updated");
  // }, [frames]);

  useLayoutEffect(() => {
    if (requestDrawings) {
      setTimeout(() => {
        const drawings = [];
        const sectionIds = [
          ...new Set(
            frames.filter((f) => f.sectionId != null).map((f) => f.sectionId),
          ),
        ];

        var edgesForTop = createEdges(scene, frames, "top", drawingTolerance);
        var edgesForSide = createEdges(scene, frames, "side", drawingTolerance);

        console.log("create edges for from and back:");

        var edgesForFront = createEdges(
          scene,
          frames,
          "left",
          drawingTolerance,
        );

        var edgesForBack = createEdges(
          scene,
          frames,
          "right",
          drawingTolerance,
        );

        const projections = {
          top: edgesForTop,
          side: edgesForSide,
          front: edgesForFront,
          back: edgesForBack,
        };

        let sharedBounds = collectAllProjectionBounds([
          projections.top,
          projections.side,
        ]);

        var svg = edgesToSVG(
          projections.top,
          frames,
          frameThickness,
          0.25,
          0.25,
          "top",
          null,
          sharedBounds,
        );

        var svg_side = edgesToSVG(
          projections.side,
          frames,
          frameThickness,
          0.25,
          0.25,
          "side",
          null,
          sharedBounds,
        );

        let frontBackSharedBounds = collectAllProjectionBounds([
          projections.front,
          projections.back,
        ]);

        frontBackSharedBounds.minY = sharedBounds.minY;
        frontBackSharedBounds.maxY = sharedBounds.maxY;

        var svg_left = edgesToSVG(
          projections.front,
          frames,
          frameThickness,
          0.25,
          0.25,
          "left",
          null,
          frontBackSharedBounds,
        );

        var svg_right = edgesToSVG(
          projections.back,
          frames,
          frameThickness,
          0.25,
          0.25,
          "right",
          null,
          frontBackSharedBounds,
        );

        function parseSVG(svgString) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(svgString, "image/svg+xml");
          const svgElement = doc.querySelector("svg");

          const viewBox = svgElement.getAttribute("viewBox");
          const [x, y, width, height] = viewBox
            ? viewBox.split(" ").map(Number)
            : [
                0,
                0,
                parseFloat(svgElement.getAttribute("width")) || 0,
                parseFloat(svgElement.getAttribute("height")) || 0,
              ];

          // Get inner content (everything except the svg wrapper)
          const content = Array.from(svgElement.children)
            .map((child) => child.outerHTML)
            .join("\n");

          return { x, y, width, height, content, viewBox };
        }

        // Parse all SVGs
        const topSVG = parseSVG(svg);
        const sideSVG = parseSVG(svg_side);
        const leftSVG = parseSVG(svg_left);
        const rightSVG = parseSVG(svg_right);

        // Define spacing between projections
        const horizontalSpacing = 0.5; // Space between left/right and center views
        const verticalSpacing = 9; // Space between top and side views

        // Calculate layout positions
        // Center views (top and side) are stacked vertically
        const centerX = leftSVG.width + horizontalSpacing;
        const topY = 0;
        const sideY = topSVG.height + verticalSpacing;

        // Left view aligned with side view
        const leftX = 0;
        const leftY = sideY;

        // Right view aligned with side view
        const rightX =
          centerX + Math.max(topSVG.width, sideSVG.width) + horizontalSpacing;
        const rightY = sideY;

        const scale = 3;

        // Calculate total canvas size
        const totalWidth =
          leftSVG.width +
          horizontalSpacing +
          Math.max(topSVG.width, sideSVG.width) +
          horizontalSpacing +
          rightSVG.width +
          5;

        const totalHeight =
          Math.max(
            topSVG.height + verticalSpacing + sideSVG.height,
            leftSVG.height,
            rightSVG.height,
          ) + 5;

        //   <rect x="0" y="0" width="${totalWidth}" height="${totalHeight}" fill="#f8f9fa" />
        //   <rect x="0" y="0" width="${totalWidth}" height="${totalHeight * 1.2}" fill="#dee1e3" />

        // Build combined SVG
        const combinedSVG = `<?xml version="1.0" encoding="UTF-8"?>
    <svg xmlns="http://www.w3.org/2000/svg"
width="297mm"                 
     height="210mm"                
     viewBox="0 0 ${totalWidth * 1.2} ${totalHeight * 1.2}"
     preserveAspectRatio="xMidYMid meet">



  <!-- Top View -->
  <g transform="translate(${centerX}, ${topY})">
    <g transform="translate(${-topSVG.x * scale}, ${-topSVG.y * scale}) scale(${scale})">
      ${topSVG.content}
    </g>
  </g>

  <!-- Side View -->
  <g transform="translate(${centerX}, ${sideY})">
    <g transform="translate(${-sideSVG.x * scale}, ${-sideSVG.y * scale}) scale(${scale})">
      ${sideSVG.content}
    </g>
  </g>
</svg>`;

        //console.log(combinedSVG);
        // console.log(svg);

        drawings.push({
          name: "Main",
          combined: "data:image/svg+xml;base64," + btoa(combinedSVG),
          top: "data:image/svg+xml;base64," + btoa(svg),
          side: "data:image/svg+xml;base64," + btoa(svg_side),
          left: "data:image/svg+xml;base64," + btoa(svg_left),
          right: "data:image/svg+xml;base64," + btoa(svg_right),
        });

        // console.log("create edges sections:");

        // sectionIds.forEach((sectionId) => {
        //   var edges = createEdges(
        //     scene,
        //     frames,
        //     "top",
        //     drawingTolerance,
        //     sectionId,
        //   );
        //   svg = edgesToSVG(
        //     edges,
        //     frames,
        //     frameThickness,
        //     0.25,
        //     0.25,
        //     "top",
        //     sectionId,
        //   );
        //   edges = createEdges(
        //     scene,
        //     frames,
        //     "side",
        //     drawingTolerance,
        //     sectionId,
        //   );
        //   svg_side = edgesToSVG(
        //     edges,
        //     frames,
        //     frameThickness,
        //     0.25,
        //     0.25,
        //     "side",
        //     sectionId,
        //   );
        //   // edges = createEdges(scene, frames, "left", drawingTolerance, sectionId);
        //   // svg_left = edgesToSVG(edges, frames, frameThickness, 0.25, 0.25, "left", sectionId);
        //   // edges = createEdges(scene, frames, "right", drawingTolerance, sectionId);
        //   // svg_right = edgesToSVG(edges, frames, frameThickness, 0.25, 0.25, "right", sectionId);
        //   drawings.push({
        //     name: "Section",
        //     top: "data:image/svg+xml;base64," + btoa(svg),
        //     side: "data:image/svg+xml;base64," + btoa(svg_side),
        //     // left: "data:image/svg+xml;base64," + btoa(svg_left),
        //     // right: "data:image/svg+xml;base64," + btoa(svg_right),
        //   });
        // });

        setDrawings(drawings);
        setRequestDrawings(false);

        if (referrer == "Project") {
          updateProjectItem({
            ResultId: null,
            Drawings: drawings,
          });
        }
      }, 100);
    }
  }, [requestDrawings, frames]);

  // useEffect(() => {
  //   if (requestDrawings) {
  //     setTimeout(() => {
  //       const drawings = [];
  //       const sectionIds = [
  //         ...new Set(
  //           frames.filter((f) => f.sectionId != null).map((f) => f.sectionId)
  //         ),
  //       ];

  //       // if(sectionIds.length == 0) return;

  //       var edges = createEdges(scene, frames, false, drawingTolerance);
  //       var svg = edgesToSVG(edges, frames, frameThickness, 0.25, 0.25, 0);

  //       edges = createEdges(scene, frames, true, drawingTolerance);
  //       var svg_side = edgesToSVG(edges, frames, frameThickness, 0.25, 0.25, 1);
  //       drawings.push({
  //         name: "Main",
  //         top: "data:image/svg+xml;base64," + btoa(svg),
  //         side: "data:image/svg+xml;base64," + btoa(svg_side),
  //       });

  //       sectionIds.forEach((sectionId) => {
  //         edges = createEdges(
  //           scene,
  //           frames,
  //           false,
  //           drawingTolerance,
  //           sectionId
  //         );
  //         svg = edgesToSVG(
  //           edges,
  //           frames,
  //           frameThickness,
  //           0.25,
  //           0.25,
  //           0,
  //           sectionId
  //         );
  //         edges = createEdges(scene, frames, true, drawingTolerance, sectionId);
  //         svg_side = edgesToSVG(
  //           edges,
  //           frames,
  //           frameThickness,
  //           0.25,
  //           0.25,
  //           1,
  //           sectionId
  //         );
  //         drawings.push({
  //           name: "Section",
  //           top: "data:image/svg+xml;base64," + btoa(svg),
  //           side: "data:image/svg+xml;base64," + btoa(svg_side),
  //         });
  //       });

  //       setDrawings(drawings);
  //       setRequestDrawings(false);

  //       if (referrer == "Project") {
  //         updateProjectItem({
  //           ResultId: null,
  //           Drawings: drawings,
  //         });
  //       }
  //     }, 100);
  //   }
  // }, [requestDrawings, frames]);

  const isFramesAttachedTogether = () => {
    const sUUID = useStore.getState().selectedUUID;
    if (sUUID && sUUID.length > 1) {
      let selectedFrames = [];
      let selectedFramesAttachedConnectionIds = [];

      for (let i = 0; i < sUUID.length; i++) {
        const frame = frames.find((f) => f.id == sUUID[i]);
        if (frame) {
          for (let j = 0; j < frame.connections.length; j++) {
            if (frame.connections[j].attachedTo != null) {
              selectedFramesAttachedConnectionIds.push(
                frame.connections[j].attachedTo,
              );
            }
          }
          selectedFrames.push(frame);
        }
      }

      for (let i = 0; i < selectedFrames.length; i++) {
        const frame = selectedFrames[i];
        let attached = false;
        for (let j = 0; j < frame.connections.length; j++) {
          if (frame.connections[j].attachedTo != null) {
            if (
              selectedFramesAttachedConnectionIds.includes(
                frame.connections[j].id,
              )
            ) {
              attached = true;
            }
          }
        }

        if (!attached) return false;
      }

      return true;
    }

    return false;
  };

  const selectedUUIDIDHasSectionId = () => {
    const sUUID = useStore.getState().selectedUUID;

    for (let i = 0; i < sUUID.length; i++) {
      const frame = frames.find((f) => f.id == sUUID[i]);
      if (frame) {
        if (frame.sectionId == "" || frame.sectionId == null) {
          return false;
        }
      }
    }

    return true;
  };

  const handleSelection = (e, f) => {
    e.stopPropagation();

    if (
      toolMode !== TOOL_MODE.SELECTION ||
      (selectedUUID?.length > 1 && selectedUUID.includes(f.id))
    )
      return;

    let selectedFrames = [f.id];
    let selectFramesInstanceIds = [];

    if (f.sectionId) {
      const framesInSection = frames
        .filter((frame) => frame.sectionId == f.sectionId && frame.id !== f.id)
        .map((frame) => frame.id);
      selectedFrames = selectedFrames.concat(framesInSection);
    }

    if (f.id) {
      if (f.sectionId) {
        selectFramesInstanceIds = frames
          .filter((frame) => frame.sectionId == f.sectionId)
          .map((frame) => frame.componentInstance);
      } else {
        selectFramesInstanceIds = frames
          .filter((frame) => frame.id == f.id)
          .map((frame) => frame.componentInstance);
      }
    }

    if (e.shiftKey) {
      setSelectedUUID(
        selectedUUID ? selectedUUID.concat(selectedFrames) : selectedFrames,
      );

      setFramesAttachedTogether(isFramesAttachedTogether());
    } else {
      setSelectedUUID(selectedFrames);
    }

    setSelectedComponentInstance(selectFramesInstanceIds);

    setSelectedUUIDIsInSection(selectedUUIDIDHasSectionId());

    queryClient.invalidateQueries([
      configuratorQueryKeys.getConfigurationInstances,
    ]);

    setShowConfigureTab(!showConfigureTab);
  };

  function baseFrameLength() {
    let length = 0;
    return frames.reduce(function (a, b) {
      return a + b["length"];
    }, 0);
  }
  function baseFrameWidth() {
    return frames[0].width;
  }

  function baseFramePosition() {
    {
      /* //<mesh position={[ !isEmpty(data.sectionId) ? (data.hasLeftFrame ? -frameThickness / 2 : frameThickness / 2 ) : 0, -(height / 2 + frameThickness + fromMM(data.baseFrame.height) / 2) - 0.005, 0]} */
    }
    let height = frames[0].height;
    // return [0,-(fromMM(height / 2) + fromMM(frameThickness) + fromMM(100) / 2) - 0.005,0];
    return [0, 0, 0];
  }

  return (
    <Draggable
      enabled={draggingEnabled}
      pickup={pickup}
      isDragging={isDragging}
      setIsDragging={setIsDragging}
      updateUnit={updateUnit}
      selectedUUID={selectedUUID}
      frames={frames}
    >
      {frames.map((f) => (
        <Frame
          key={f.id}
          data={f}
          selected={selectedUUID?.includes(f.id)}
          setPickup={setPickup}
          isDragging={isDragging}
          handleSelection={handleSelection}
          updateUnit={updateUnit}
          frames={frames}
        ></Frame>
      ))}
      {/* //<mesh position={[ !isEmpty(data.sectionId) ? (data.hasLeftFrame ? -frameThickness / 2 : frameThickness / 2 ) : 0, -(height / 2 + frameThickness + fromMM(data.baseFrame.height) / 2) - 0.005, 0]} */}
      {/* <mesh
        position={baseFramePosition()}
        userData={{ type: "baseFrame" }}
      >        <boxGeometry args={[fromMM(baseFrameLength()), fromMM(100), fromMM(baseFrameWidth())]} />
        <meshStandardMaterial color="grey" />
      </mesh> */}
    </Draggable>
  );
}
